// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 09-data-brokering.md
// @greenlight-spec: decisions/0027-integration-grant-approval-policy-placement.md
// @greenlight-spec: decisions/0039-injected-grant-is-disclosure.md
// @greenlight-spec: 48-user-passthrough.md#catalog-profiles-and-credentials
import * as React from 'react';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { api } from '@/lib/api';
import type { CredentialRequiredGroup, IntegrationListItem } from '@greenlight/shared';
import { isSecretlessAuthMode, normalizeCredentialRole } from '@greenlight/shared';

import {
  CredentialSecretInputs,
  EMPTY_SECRET_DRAFT,
  clientIdLabelFor,
  clientIdPlaceholderFor,
  composeSecretDraft,
  secretDraftError,
  secretNounFor,
  secretPlaceholderForCatalog,
  secretShapeFor,
  type SecretDraft,
} from './CredentialSecretInputs';
import { credentialRoleFor } from './catalogRegistration';
import { RequiredGroupsPicker, toRequiredGroupRefs } from './RequiredGroupsPicker';

const CREDENTIAL_SLUG_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;

export interface AddCredentialDialogProps {
  /** The integration to add a credential to, or null when closed. */
  integration: IntegrationListItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Adds a credential to an existing integration — for upstreams that expose
 * multiple keys (e.g. a read and a write GitHub App installation). Slug + scope
 * + write-only secret; the secret is stored in Key Vault and never read back
 * (docs/28 § /integrations → Add credentials).
 */
export function AddCredentialDialog({
  integration,
  open,
  onOpenChange,
}: AddCredentialDialogProps): React.JSX.Element {
  const queryClient = useQueryClient();
  const addCredential = api.integrations.addCredential.useMutation();
  const [slug, setSlug] = React.useState('');
  const [scope, setScope] = React.useState('');
  const [secretDraft, setSecretDraft] = React.useState<SecretDraft>(EMPTY_SECRET_DRAFT);
  const [approvalAuto, setApprovalAuto] = React.useState(false);
  const [requiredGroups, setRequiredGroups] = React.useState<CredentialRequiredGroup[]>([]);
  const [upstreamClientId, setUpstreamClientId] = React.useState('');
  const [role, setRole] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) {
      setSlug('');
      setScope('');
      setSecretDraft(EMPTY_SECRET_DRAFT);
      setApprovalAuto(false);
      setRequiredGroups([]);
      setUpstreamClientId('');
      setRole('');
      setError(null);
    }
  }, [open]);

  const isInjected = integration?.delivery_mode === 'injected';
  // A user passthrough credential is one upstream OAuth client: its id rides
  // here as non-secret config and its secret is the credential (docs/48).
  const isPassthrough = integration?.auth_mode === 'user_delegated';
  const secretless = isSecretlessAuthMode(integration?.auth_mode);
  const secretShape = secretShapeFor(
    integration?.auth_mode,
    integration?.token_endpoint_auth_method,
  );
  const clientIdLabel = clientIdLabelFor(
    integration?.catalog_key,
    integration?.auth_mode,
    integration?.auth_config?.['mechanism'],
  );
  // Each credential of a role-per-credential profile (Snowflake) is one role, fixed once saved.
  const roleRule = credentialRoleFor(
    integration?.catalog_key,
    integration?.auth_mode,
    integration?.auth_config?.['mechanism'],
  );
  const clientIdPlaceholder = clientIdPlaceholderFor(
    integration?.catalog_key,
    integration?.auth_mode,
    integration?.auth_config?.['mechanism'],
  );
  const secretExamplePlaceholder = secretPlaceholderForCatalog(
    integration?.catalog_key,
    integration?.auth_mode,
    integration?.auth_config?.['mechanism'],
  );
  const secretNoun = isPassthrough
    ? 'client secret'
    : secretNounFor(secretShape, integration?.auth_mode ?? '');

  const existingSlugs = React.useMemo(
    () => new Set((integration?.credentials ?? []).map((c) => c.slug)),
    [integration],
  );

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!integration) return;
    const s = slug.trim();
    const draftError = secretless
      ? null
      : secretDraftError(secretDraft, {
          shape: secretShape,
          clientIdLabel,
          secretNoun,
          required: true,
        });
    if (!s || !scope.trim()) {
      setError('Slug and scope are both required.');
      return;
    }
    if (isPassthrough && !upstreamClientId.trim()) {
      setError(
        'Enter the client (application) ID of the upstream registration this credential presents.',
      );
      return;
    }
    const normalizedRole = roleRule ? normalizeCredentialRole(role) : null;
    if (roleRule && normalizedRole === null) {
      setError(
        `Enter the ${roleRule.label.toLowerCase()}: letters, digits, _ and $ only (a role created with quotes is not supported).`,
      );
      return;
    }
    if (roleRule && normalizedRole !== null && roleRule.blocked.includes(normalizedRole)) {
      setError(`${normalizedRole} is never reachable through OAuth; name a narrower role.`);
      return;
    }
    if (draftError) {
      setError(draftError);
      return;
    }
    if (!CREDENTIAL_SLUG_RE.test(s)) {
      setError('Slug must be lowercase alphanumeric and hyphens.');
      return;
    }
    if (existingSlugs.has(s)) {
      setError(`This integration already has a "${s}" credential.`);
      return;
    }
    try {
      const res = await addCredential.mutateAsync({
        params: { id: integration.id },
        body: {
          slug: s,
          scope: scope.trim(),
          ...(secretless ? {} : { secret: composeSecretDraft(secretDraft, secretShape) }),
          ...(isPassthrough
            ? {
                authConfig: {
                  client_id: upstreamClientId.trim(),
                  ...(normalizedRole !== null ? { role: normalizedRole } : {}),
                },
              }
            : {}),
          approvalMode: approvalAuto ? 'auto' : 'manual',
          ...(requiredGroups.length > 0
            ? { requiredGroups: toRequiredGroupRefs(requiredGroups) }
            : {}),
        },
      });
      if (res.status >= 300) throw res;
      toast.success(`Credential "${s}" added.`);
      await queryClient.invalidateQueries({ queryKey: ['integrations'] });
      onOpenChange(false);
    } catch {
      setError('Failed to add credential. Please try again.');
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add credential</DialogTitle>
          <DialogDescription>
            {integration
              ? `A new credential slug on ${integration.name}. Apps bind grants to the slug; the secret is write-only.`
              : ''}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="add-cred-slug">Slug</Label>
            <Input
              id="add-cred-slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="e.g. repo-write"
              className="font-mono"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="add-cred-scope">Scope</Label>
            <Input
              id="add-cred-scope"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              placeholder="What this key grants (free text)"
            />
          </div>
          {isPassthrough && (
            <div className="space-y-1.5">
              <Label htmlFor="add-cred-upstream-client-id">{clientIdLabel}</Label>
              <Input
                id="add-cred-upstream-client-id"
                value={upstreamClientId}
                onChange={(e) => setUpstreamClientId(e.target.value)}
                placeholder={
                  clientIdPlaceholder ?? 'Application (client) ID of the upstream registration'
                }
                className="font-mono"
                autoComplete="off"
              />
              <p className="text-[11.5px] leading-snug text-muted-foreground">
                The upstream registration this credential presents. Every app granted it reaches
                exactly that registration’s permissions, as each signed-in user.
              </p>
            </div>
          )}
          {roleRule && (
            <div className="space-y-1.5">
              <Label htmlFor="add-cred-role">{roleRule.label}</Label>
              <Input
                id="add-cred-role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                placeholder={roleRule.example}
                className="font-mono"
                autoComplete="off"
              />
              <p className="text-[11.5px] leading-snug text-muted-foreground">{roleRule.hint}</p>
            </div>
          )}
          {secretless ? (
            <p className="rounded-gl border border-dashed border-border px-3 py-2 text-[11.5px] leading-snug text-muted-foreground">
              No secret to paste — this credential is a grant-binding handle. Greenlight
              authenticates as its platform identity.
            </p>
          ) : (
            <CredentialSecretInputs
              idPrefix="add-cred"
              shape={secretShape}
              clientIdLabel={clientIdLabel}
              clientIdPlaceholder={clientIdPlaceholder}
              secretNoun={secretNoun}
              secretExamplePlaceholder={secretExamplePlaceholder}
              draft={secretDraft}
              onChange={setSecretDraft}
            />
          )}
          {isInjected && (
            <p
              className="flex items-start gap-1.5 rounded-gl border border-border bg-muted/40 p-3 text-[11.5px] leading-relaxed text-foreground"
              data-testid="add-cred-injected-disclosure"
            >
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span>
                This integration is injected: granted agents and developers can obtain this value,
                including locally. Prefer a dedicated least-privilege upstream account — rotation is
                the only revocation.
              </span>
            </p>
          )}
          <div className="space-y-1.5 rounded-gl border border-border bg-muted/30 p-3">
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="add-cred-approval" className="cursor-pointer">
                Auto-approve grants
              </Label>
              <Switch
                id="add-cred-approval"
                checked={approvalAuto}
                onCheckedChange={setApprovalAuto}
                aria-label="Auto-approve grants for this credential"
              />
            </div>
            <p className="text-[11.5px] leading-snug text-muted-foreground">
              On, every app’s backed grant to this credential auto-approves at merge. Off (default),
              grants land in IT’s review queue.
            </p>
            <div className="border-t border-border pt-3">
              <RequiredGroupsPicker
                idPrefix="add-cred"
                value={requiredGroups}
                onChange={setRequiredGroups}
                enabled={open}
              />
            </div>
          </div>
          {error && <p className="text-[11.5px] text-destructive">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={addCredential.isPending}>
              {addCredential.isPending ? 'Adding…' : 'Add credential'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
